import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { API } from '../constants';
import { REQUEST_PENDING, REQUEST_SUCCESS } from './network/managedRequest';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { pbRequestReset, pbRequestSuccess } from './pbRequestSlice';
import { PbRequest } from './pbRequest';
import { PbDispatch, PbGetState } from './index';

export interface PartnerContactDto {
    phone?: string;
    name?: string;
    company: string;
    supplementary_line_1?: string;
    supplementary_line_2?: string;
    supplementary_line_3?: string;
    street_and_house_number: string;
    city: string;
    postal_code: string;
    country: string;
    country_iso: string;
    last_changed_at?: number;
}

interface PartnerContactsStore {
    contractualContract: PartnerContactDto | undefined;
    creditNoteRecipient: PartnerContactDto | undefined;
}

const initialState: PartnerContactsStore = {
    contractualContract: undefined,
    creditNoteRecipient: undefined,
};

export const partnerContactsSlice = createSlice({
    name: 'partnerContacts',
    initialState,
    reducers: {
        resetAll(state) {
            state.contractualContract = undefined;
            state.creditNoteRecipient = undefined;
        },
        setContractualContract(
            state: PartnerContactsStore,
            action: PayloadAction<PartnerContactDto>
        ) {
            state.contractualContract = action.payload;
        },
        setCreditNoteRecipient(
            state: PartnerContactsStore,
            action: PayloadAction<PartnerContactDto | undefined>
        ) {
            state.creditNoteRecipient = action.payload;
        },
    },
});

const { resetAll, setContractualContract, setCreditNoteRecipient } =
    partnerContactsSlice.actions;

export function ResetPartnerContacts() {
    return async (dispatch) => {
        dispatch(resetAll());
        dispatch(pbRequestReset(PbRequest.GET_CONTRACTUAL_CONTACT));
        dispatch(pbRequestReset(PbRequest.GET_CREDIT_NOTE_RECIPIENT));
    };
}

export function LoadContractualContact() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        if (
            getState().pbRequest[PbRequest.GET_CONTRACTUAL_CONTACT].status ===
            REQUEST_PENDING
        ) {
            return;
        }
        const request = { method: 'GET' };
        const result =
            await processRequestForAccountingAreaWithReplacement<PartnerContactDto>(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/contractual_contact`,
                request,
                PbRequest.GET_CONTRACTUAL_CONTACT
            );
        dispatch(setContractualContract(result));
    };
}

export function SaveContractualContact(contractualContact: PartnerContactDto) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        const request = {
            method: 'PUT',
            body: JSON.stringify(contractualContact),
        };
        const result = await processRequestForAccountingAreaWithReplacement(
            dispatch,
            `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/contractual_contact`,
            request,
            PbRequest.PUT_CONTRACTUAL_CONTACT
        );

        if (
            getState().pbRequest[PbRequest.PUT_CONTRACTUAL_CONTACT].status ===
            REQUEST_SUCCESS
        ) {
            dispatch(LoadContractualContact());
        }

        return result;
    };
}

export function LoadCreditNoteRecipient() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        if (
            getState().pbRequest[PbRequest.GET_CREDIT_NOTE_RECIPIENT].status ===
            REQUEST_PENDING
        ) {
            return;
        }
        const request = { method: 'GET' };
        try {
            const result =
                await processRequestForAccountingAreaWithReplacement<PartnerContactDto>(
                    dispatch,
                    `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/credit_note_recipient`,
                    request,
                    PbRequest.GET_CREDIT_NOTE_RECIPIENT
                );
            dispatch(setCreditNoteRecipient(result));
        } catch (error) {
            if (error.response?.status === 404) {
                dispatch(pbRequestSuccess(PbRequest.GET_CREDIT_NOTE_RECIPIENT));
                dispatch(setCreditNoteRecipient(undefined));
                return;
            }
            throw error;
        }
    };
}

export function SaveCreditNoteRecipient(
    creditNoteRecipient: PartnerContactDto
) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        const request = {
            method: 'PUT',
            body: JSON.stringify(creditNoteRecipient),
        };
        const result = await processRequestForAccountingAreaWithReplacement(
            dispatch,
            `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/credit_note_recipient`,
            request,
            PbRequest.PUT_CREDIT_NOTE_RECIPIENT
        );

        if (
            getState().pbRequest[PbRequest.PUT_CREDIT_NOTE_RECIPIENT].status ===
            REQUEST_SUCCESS
        ) {
            dispatch(LoadCreditNoteRecipient());
        }

        return result;
    };
}

export function DeleteCreditNoteRecipient() {
    return async (dispatch: PbDispatch) => {
        const request = {
            method: 'DELETE',
        };
        return await processRequestForAccountingAreaWithReplacement(
            dispatch,
            `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/credit_note_recipient`,
            request,
            PbRequest.PUT_CREDIT_NOTE_RECIPIENT
        );
    };
}

export default partnerContactsSlice.reducer;
